import { ClockIcon, IdentificationIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType, setHeader } from 'actions'
import cloneDeep from 'clone-deep'
import { EmailValue, LoanPartiesEmailTo } from 'components/EmailTo'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import { BaseFile, COMPANY_DNS, COMPANY_TITLE, InputType, IS_COMPANY_CC, loanOptionsConstants, TimeZone } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { DateTime } from 'luxon'
import { loanOverviewAndStepLogic } from 'pages/LoanOverview/loanOverviewAndStepLogic'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Prompt } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  appraisalV2CreateLog,
  cancelAppraisalInfoV2,
  changeAppraisal2Status,
  createAppraisalInfoV2,
  deleteAppraisalInfoV2,
  // deleteAppraisalInfoV2,
  getAppraisalInformationFeedV2,
  getAppraisalScopeJobAddons,
  getBorrowerEliteCredential,
  openS3Document,
  sendOrderAppraisalFeasibilitySignRequest,
  updateAppraisalInfoV2,
} from 'services'
import Api from 'services/api'
import { svgLoading } from 'stories/assets'
import { Button, DocumentFile, FormTable, InputFileTable, Modal, Select, TextArea } from 'stories/components'
import { CategoryDocument, CategoryDocuments } from 'stories/components/CategoryFileTable/CategoryFileTable'
import { Tooltip } from 'stories/components/Tooltip/Tooltip'
import {
  confirm,
  formatDate,
  formatDateYMD,
  formatTime,
  InputValidate,
  insertAfter,
  openAuditLog,
  removeComma,
  validateEmail,
} from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

import { defaultInputs, isShowEntityName } from './constants'
import { AppraisalNation, EliteValuation, handleFailedDocsNotification, Inspectify, Trinity } from './Modals'
import {
  appraisal2IntegrationTypes,
  Appraisal2StatusType,
  Appraisal2ThridPartyType,
  Appraisal2ThridPartyTypeForInit,
  AppraisalDocumentCategories,
  AppraisalScopeThirdpartyTypes,
  appraisalScopeTitles,
  brokerCanNotEditFields,
  budgetReviewAmounts,
  budgetReviewOptions,
  cardFields,
  contactPersonFields,
  feasibilityACHKeys,
  feasibilityCCKeys,
  feasibilityKeys,
  feasibilityOrderTypes,
  feasibilityRequiredKeys,
  feasibilityStudyAmounts,
  feasibilityStudyOptions,
  header,
  IntegrationType,
  integrationTypeTitles,
  integrationTypeTitlesReversed,
  IntegrationTypeValues,
  mapLoanToAppraisal,
  mapOrderTypeFormOptions,
  OrderAppraisalOrderTypeOptions,
  OrderAppraisalOrderTypeOptionsByVendor,
  paymentInformationFormKeys,
  vendorToIntegrationType,
} from './types'

const AddAppraisalInformationV2 = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<Array<any>>([])
  const [logs, setLogs] = useState<Array<any>>([])

  const [thirdpartyType, setThirdpartyType] = useState<string | null>(null)
  const [thirdpartyData, setThirdpartyData] = useState<any>(null)

  const [feed, setFeed] = useState<any>({})
  const [inputs, setInputs] = useState<any>({})
  const [editingLog, setEditingLog] = useState('')

  const [action, setAction] = useState('')
  const [editOrderID, setEditOrderID] = useState('All')
  const [orderIDs, setOrderIDs] = useState<Array<any>>([])
  const [cancelEditFlag, setCancelEditFlag] = useState(0)
  const [updateChangeMade, setUpdateChangeMade] = useState(false)
  const [docFiles, setDocFiles] = useState<Array<DocumentFile>>([])

  const [credentials, setCredentials] = useState<Record<IntegrationType, any[]>>()
  const [docTypes, setDocTypes] = useState<Record<IntegrationType, string[]>>()
  const [jobTypes, setJobTypes] = useState<Record<IntegrationType, string[]>>()
  const [additionalTypes, setAdditionalTypes] = useState<Record<IntegrationType, any>>()
  const [additionalInputs, setAdditionalInputs] = useState<Record<IntegrationType, Record<string, InputType>>>()
  const [jobTypeAddons, setJobTypeAddons] = useState<Record<IntegrationType, Record<string, string[]>>>({} as any)

  const [messageToModal, setMessageToModal] = useState(false)
  const [notifiers, setNotifiers] = useState<EmailValue>({})
  const [toEmails, setToEmails] = useState<any>([])
  const [appraiser, setAppraiser] = useState<Record<string, any>>({})
  const documentsRef = useRef()
  const formRef = useRef()
  const dispatch = useDispatch()

  const [upcomingStatus, setUpcomingStatus] = useState<null | Record<string, any>>(null)

  const { hasPermission } = usePermissions()

  const adminGroup = hasPermission('ADMIN_TO_AE_PROFILE_PERMISSION')
  const hasAdminPermission = hasPermission('VALUATION_VENDOR_VISIBLE')

  const [loan, { parties }, vendor] = useSelector((state: any) => [state.loan, state.loanDetail, state.vendor])

  const orderAppraisalVendorOptions = useSelector(
    (state: any) => state.globalConfigReducer[GlobalConfigType.ORDER_VALUATION],
  )

  useEffect(() => {
    if (!upcomingStatus) return
    changeStatus(upcomingStatus.id, upcomingStatus.value, upcomingStatus.additional)
    setUpcomingStatus(null)
  }, [upcomingStatus])

  const onSendSignLink = async (id: number, feasibilityData: Record<string, any>) => {
    if (removeComma(feasibilityData.feasibilityCCStudyFee) <= 0) {
      return toast('Payment Amount is Required!', { type: 'error' })
    }

    if (mapOrderTypeFormOptions[feasibilityData.orderType]?.indexOf(feasibilityData.appraisalForm) === -1) {
      return toast(`'Appraisal Form' is Required!`, { type: 'error' })
    }
    if (!validateEmail(feasibilityData.feasibilityEmailToLink)) {
      return toast(`Email to Send Link is Required!`, { type: 'error' })
    }
    if (!id) {
      id = await (formRef.current as any).submit()
      if (!id) return
    }
    setLoading(true)
    await sendOrderAppraisalFeasibilitySignRequest(id, feasibilityData)

    const orderTitle = getOrderTitle(feasibilityData.orderType)
    setUpcomingStatus({ id, value: 'pendingPayment', additional: `${orderTitle} Payment Requested` })
    toast(`${orderTitle} Payment Request is sent.`, { type: 'info' })
    setLoading(false)
  }

  const cancelOrder = async () => {
    if (editOrderID === 'All') {
      return toast('Order ID is required!', { type: 'error' })
    }

    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Are you sure want to cancel order?
        <br />
        <span className="text-gray-600 text-[15px] font-bold">Order ID: {editOrderID}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    onConfirmCancelOrder()
  }

  const onChangeEmail = (data: EmailValue) => {
    setNotifiers(data)
  }

  const createLog = async (fromModal: boolean) => {
    if (editingLog.length === 0) {
      return toast('Message is Required!', { type: 'error' })
    }
    if (editOrderID === 'All') {
      return toast('Order ID is required!', { type: 'error' })
    }

    if (!fromModal && adminGroup) {
      const edit = data.find((item) => item.id == editOrderID)
      let temp: any = {},
        emails = []
      temp[edit.createdBy] = 'to'
      emails.push({ party: 'Order Creator', email: edit.createdBy })
      temp[edit.info.loanOfficerEmail] = 'to'
      emails.push({ party: 'Order Loan Officer', email: edit.info.loanOfficerEmail })

      if (edit.info.processorEmail) {
        temp[edit.info.processorEmail] = 'to'
        emails.push({ party: 'Order Loan Processor', email: edit.info.processorEmail })
      }
      if (appraiser) emails.push({ party: 'Appraiser', email: appraiser })
      setToEmails(emails)
      setNotifiers(temp)
      setMessageToModal(true)
      return true
    }
    setAction('createLog')
    setLoading(true)
    const res = await appraisalV2CreateLog({ id: editOrderID, message: editingLog, files: docFiles, notifiers })
    if (res.success) {
      let temp = cloneDeep(logs)
      temp.push(res.data)
      setLogs(temp)
    }
    setEditingLog('')
    setDocFiles([])
    setAction('')
    setLoading(false)
    setMessageToModal(false)
  }

  const renderDocuments = (value: CategoryDocument[] = [], onChange: Function, input: InputType) => {
    const integrationType = (input as any).integrationType as IntegrationType
    const types = !docTypes || !integrationType ? null : docTypes[integrationType]

    return (
      <CategoryDocuments
        ref={documentsRef}
        documents={value}
        onChange={onChange}
        setLoading={setLoading}
        type={'OrderAppraisal'}
        categories={AppraisalDocumentCategories}
        types={types}
      />
    )
  }

  const getOrderTitle = (orderType: string) => {
    return orderType.split(' ')[0]
  }

  const inputLogic = (_data: any, id: number = 0, key: string = '', values: Record<string, any> = {}) => {
    if (id > 0 && key !== '') {
      setUpdateChangeMade(true)
    }
    let data = cloneDeep(_data)

    const amc = data.amc.value
    const integrationType = integrationTypeTitlesReversed[amc] as IntegrationType
    const clientLenderAccount = data.clientLenderAccount.value

    const additionalOptions = additionalTypes?.[integrationType]?.[clientLenderAccount || 'main']
    if (additionalOptions) {
      Object.keys(additionalOptions).forEach((key) => {
        if (!data[key]) return
        data[key].options = additionalOptions[key]
      })
    }

    if (integrationType && additionalInputs && additionalInputs[integrationType]) {
      const elements = additionalInputs[integrationType]
      Object.keys(elements)
        .reverse()
        .forEach((key) => {
          data = insertAfter(data, 'underConstruction', key, elements[key])
          if (values[key]) {
            data[key].value = values[key]
            data[key].error = InputValidate(data[key])
          }
        })
    } else {
      const defaultKeys = Object.keys(defaultInputs())
      Object.keys(data).forEach((key) => {
        if (!defaultKeys.includes(key)) delete data[key]
      })
    }

    if (key == 'amc') {
      if (Object.keys(OrderAppraisalOrderTypeOptionsByVendor).includes(_data.amc.value)) {
        data.orderType.value = ''
        data.orderType.options = OrderAppraisalOrderTypeOptionsByVendor[_data.amc.value]
      } else if (additionalTypes?.[integrationType]?.[clientLenderAccount || 'main']?.orderType) {
        data.orderType.value = ''
        data.orderType.options = additionalTypes?.[integrationType]?.[clientLenderAccount || 'main']?.orderType
      } else data.orderType.options = OrderAppraisalOrderTypeOptions
    }

    const orderType = data.orderType.value
    const isFeasibility = feasibilityOrderTypes.includes(orderType)
    if (isFeasibility) {
      data.feasibilityTitle.title = `${getOrderTitle(orderType)} Payment Method`
    }
    paymentInformationFormKeys.forEach((key) => (data[key].visible = !isFeasibility))
    let isSendInvoice = data.isSendInvoice.value
    if (isSendInvoice === undefined) isSendInvoice = true
    if (!isFeasibility) {
      cardFields.map((key) => {
        data[key].visible = !isSendInvoice
      })
      data['paymentLinkEmail'].visible = isSendInvoice
    }
    data['isSendInvoice'].span = isSendInvoice ? 2 : 4

    if (!adminGroup) data['paymentAmount'].visible = false

    if (data['contactSameAsBorrower'].value) {
      Object.keys(contactPersonFields).map((key) => {
        const map = contactPersonFields[key]
        try {
          if (data[key]?.value?.length > 0) {
          } else {
            data[key].value = data[map].value
            data[key].error = InputValidate(data[key])
          }
        } catch {}
      })
    }

    data.purchaseAmount.visible = data.purpose.value === 'Purchase'
    data.appraisalScopeJobAddons.visible = false
    let appraisalFormOptions = []
    data.propertyType.options = loanOptionsConstants.propertyType

    data.documents.integrationType = integrationType

    try {
      if (amc == 'Vision VMC') {
        if (clientLenderAccount) {
          appraisalFormOptions = (jobTypes as any)[integrationType][clientLenderAccount][orderType] || []
        }

        if (orderType && clientLenderAccount) {
          const propertyTypes = additionalTypes?.[integrationType]?.[clientLenderAccount]?.['propertyTypes']
          data.propertyType.options = propertyTypes?.[orderType] || propertyTypes?.['Residential Appraisal']
        } else {
          data.propertyType.options = []
          data.propertyType.value = ''
        }
      } else {
        if (orderType == 'Residential Appraisal') {
          if (appraisalScopeTitles.includes(amc)) {
            if (clientLenderAccount) {
              const integrationType = integrationTypeTitlesReversed[amc] as IntegrationType
              appraisalFormOptions = (jobTypes as any)[integrationType][clientLenderAccount] || []
              data.appraisalScopeJobAddons.visible = true

              const jobType = data.appraisalForm.value
              if (jobType) {
                let addons = jobTypeAddons?.[integrationType]?.[jobType]
                data.appraisalScopeJobAddons.isLoading = !addons

                if (!addons) {
                  addons = []
                  onUpdateAppraisalScopeJobType(integrationType, clientLenderAccount, jobType)
                }
                data.appraisalScopeJobAddons.options = addons
              }
            }
          } else appraisalFormOptions = mapOrderTypeFormOptions[orderType]
        } else appraisalFormOptions = mapOrderTypeFormOptions[orderType]
      }
    } catch (e) {
      console.log(e)
    }
    data.appraisalForm.options = appraisalFormOptions || []

    data.underConstruction.visible = ['Commercial Appraisal', 'Residential Appraisal'].indexOf(orderType) !== -1

    if (isFeasibility) {
      ;[...feasibilityKeys, ...feasibilityACHKeys, ...feasibilityCCKeys, 'feasibilitySignKey'].forEach(
        (key) => (data[key].visible = false),
      )
      data.feasibilityTitle.visible = true
      data.feasibilityEmailToLink.visible = true
      data.feasibilitySignKey.visible = true
      data.feasibilityCCStudyFee.visible = true
      feasibilityRequiredKeys.forEach((key) => (data[key].required = true))

      if (key === 'appraisalForm') {
        let amount = 0
        const appraisalForm = data.appraisalForm.value
        if (appraisalForm) {
          let index = feasibilityStudyOptions.indexOf(appraisalForm)
          if (index != -1) {
            amount = feasibilityStudyAmounts[index]
          } else {
            const index = budgetReviewOptions.indexOf(appraisalForm)
            if (index != -1) {
              amount = budgetReviewAmounts[index]
            }
          }
        }
        data.feasibilityCCStudyFee.value = amount
      }

      const feasibilityData: Record<string, any> = {
        feasibilityEmailToLink: data.feasibilityEmailToLink.value,
        orderType: data.orderType.value,
        appraisalForm: data.appraisalForm.value,
        feasibilityCCStudyFee: data.feasibilityCCStudyFee.value,
      }
      data.feasibilitySignKey.render = () => (
        <Button color="gray" onClick={() => onSendSignLink(id, feasibilityData)}>
          Send Payment Link
        </Button>
      )

      if (hasAdminPermission) {
        data['feasibilityCCStudyFee'].disabled = false
      } else {
        data['feasibilityCCStudyFee'].disabled = true
      }
    } else {
      ;[...feasibilityKeys, ...feasibilityACHKeys, ...feasibilityCCKeys, 'feasibilitySignKey'].forEach(
        (key) => (data[key].visible = false),
      )
    }
    data.feasibilitySignKey.visible = isFeasibility && adminGroup
    data.underConstructionAlert.visible =
      data.underConstruction.visible === true && data.underConstruction.value === true

    data.documents.render = renderDocuments

    Object.keys(vendorToIntegrationType).forEach((vendorKey) => {
      if (vendor[vendorKey]) return
      const integrationType = vendorToIntegrationType[vendorKey]
      const { options } = data.amc
      delete options[(integrationTypeTitles as any)[integrationType]]
    })

    if (data.amc.visible) {
      if (
        Object.values(appraisal2IntegrationTypes)
          .map((v) => integrationTypeTitles[v])
          .includes(data.amc.value)
      ) {
        data.entityName.visible = true
        if (data.entityName.value === undefined) data.entityName.value = feed.entityTitle
      } else {
        data.entityName.visible = false
      }
      if (data.amc.value === integrationTypeTitles[IntegrationType.EliteValuation]) {
        data.processorEmail.required = true
      } else {
        data.processorEmail.required = false
        data.processorEmail.error = ''
      }
      if (data.amc.value === 'Trinity') {
        data.trinityOrderID.visible = true
      } else {
        data.trinityOrderID.visible = false
      }
    }

    if (
      credentials &&
      ((data.orderType.value === 'Residential Appraisal' && Object.values(integrationTypeTitles).includes(amc)) ||
        amc == integrationTypeTitles[IntegrationType.VisionVMC])
    ) {
      const index = Object.values(integrationTypeTitles).indexOf(data.amc.value)
      const credential = credentials[IntegrationTypeValues[index]]

      data.dividerClientLender.visible = true
      data.clientLenderAccount.visible = true
      if (data.amc.value != 'Elite Valuations') data.clientLenderCompany.visible = true
      else data.clientLenderCompany.visible = false

      const accountOptions: any = {}
      credential.map((v: any) => {
        if (v.value.type === 'main') accountOptions['main'] = COMPANY_TITLE
        if (v.value.type === 'broker') accountOptions['broker'] = `Broker (${parties.broker.email})`
        if (v.value.type === 'creator') accountOptions['creator'] = `Loan Creator (${parties.creator.email})`
        if (v.value.type === 'loanOfficer')
          accountOptions['loanOfficer'] = `Loan Officer (${parties.loanOfficer.email})`
        if (v.value.type === 'loanProcessor')
          accountOptions['loanProcessor'] = `Loan Processor (${parties.loanProcessor.email})`
        if (v.value.type === 'myAccount') accountOptions['myAccount'] = 'My Account'
      })
      data.clientLenderAccount.options = accountOptions

      const options: any = {}
      credential
        .filter((v: any) => v.value.type == data.clientLenderAccount.value)
        .map((v: any) => {
          if (v.value.clientid) options[v.value.clientid] = v.desc
          else options[v.value.type] = v.desc
        })
      data.clientLenderCompany.options = options
    } else {
      data.dividerClientLender.visible = false
      data.clientLenderAccount.visible = false
      data.clientLenderCompany.visible = false
    }

    if (data.appraisalForm.value === 'Draw Inspection') {
      data.dividerPay.visible = false
      data.isSendInvoice.visible = false
      data.paymentLinkEmail.visible = false
    }

    if (data.notifications.value?.length > 1) {
    } else {
      data.notifications.value =
        data.appraisalForm.value === 'Draw Inspection'
          ? [`draws@${COMPANY_DNS}`]
          : [`${IS_COMPANY_CC ? 'processing' : 'loansetup'}@${COMPANY_DNS}`]
    }

    const showEntityName = isShowEntityName(loan.borrowerType, amc)
    data.borrowerFirstName.visible = !showEntityName
    data.borrowerLastName.visible = !showEntityName
    data.entityName.visible = showEntityName
    data.contactSameAsBorrower.visible = !showEntityName

    return data
  }

  const onUpdateAppraisalScopeJobType = async (
    integrationType: IntegrationType,
    lenderAccount: string,
    jobType: string,
  ) => {
    const addons = await getAppraisalScopeJobAddons({ integrationType, lenderAccount, jobType })
    const newJobTypeAddons = cloneDeep(jobTypeAddons)
    if (!newJobTypeAddons?.[integrationType]) newJobTypeAddons[integrationType] = {}
    newJobTypeAddons[integrationType][jobType] = addons

    // console.log('newJobTypeAddons', newJobTypeAddons)
    setJobTypeAddons(newJobTypeAddons)
    ;(formRef.current as any).updateInputs({
      appraisalScopeJobAddons: {
        options: addons,
        isLoading: false,
      },
    })
  }

  useEffect(() => {
    if (!feed.loanNumber) return

    onFeedUpdate()
  }, [feed])

  const onFeedUpdate = async () => {
    const temp: any = defaultInputs()
    let _loanNumber = feed.loanNumber
    if (feed.byteproFileName !== 0) _loanNumber = feed.byteproFileName
    temp.loanNumber.value = _loanNumber
    temp.loanOfficerEmail.value = feed.loanOfficer
    temp.processorEmail.value = feed.loanProcessor
    temp.orderDate.value = formatDateYMD()
    temp.clientDueDate.value = DateTime.now().setZone(TimeZone).plus({ days: 7 }).toFormat('yyyy-MM-dd')
    temp.entityName.value = feed.entityTitle
    temp.borrowerFirstName.value = feed.firstName
    temp.borrowerLastName.value = feed.lastName
    temp.borrowerEmail.value = feed.email
    temp.borrowerPhone.value = feed.phone
    temp.propertyAddress.value = feed.subjectPropertyAddress
    temp.propertyCounty.value = feed.propertyCounty
    temp.propertyType.value = feed.propertyType
    temp.purchaseAmount.value = feed.propertyPurchasePrice
    temp.purpose.value = feed.transactionType
    temp.occupancy.value = mapLoanToAppraisal.occupancy[feed.proposedOccupancy]
    brokerCanNotEditFields.map((key: string) => {
      temp[key].disabled = !adminGroup
    })
    temp.amc.visible = hasAdminPermission
    temp.trinityOrderID.visible = hasAdminPermission
    temp.notifications.visible = hasAdminPermission
    temp.entityName.visible = false
    setInputs(await inputLogic(temp))
  }

  const changeStatus = async (id: number, value: string, additional: string = '') => {
    setLoading(true)
    setLoanNumber()
    const res = await changeAppraisal2Status({ id, value, additional })
    if (!additional) toast('Successfully Updated!', { type: 'success' })
    setLoading(false)
    if (res.success) {
      let temp = cloneDeep(data)
      temp.map((item, index) => {
        if (item.id === id) {
          temp[index].status = value
        }
      })
      dispatch(
        setHeader({
          activeChangeFlag: true,
        }),
      )
      setData(temp)
    }
  }

  const showHistory = (id: number, key: string = 'status') => {
    const options = {
      table: 'Appraisal2',
      field: key,
      keys: {
        field: key,
        id,
      },
    }
    openAuditLog(options)
  }

  const renderStatus = (item: any) => {
    const load = action === `status-change-${item.id}`
    return (
      <div className="flex items-center">
        <div className={`w-[160px] -mb-4`}>
          <Select
            key={item.id}
            id={`loan-status-${item.id}`}
            size={3}
            title=""
            options={Appraisal2StatusType}
            disabled={load}
            value={item.status}
            onChange={(value) => changeStatus(item.id, value)}
          />
        </div>
        <span className="ml-2 text-gray-500 cursor-pointer">
          <ClockIcon className="w-4 h-4" onClick={() => showHistory(item.id)} />
        </span>
      </div>
    )
  }

  const getHeader = useMemo(() => {
    if (hasAdminPermission) return header()

    const newHeader = header()
    const index = newHeader.findIndex((v) => v.key == 'vendor')
    if (index != -1) newHeader.splice(index, 1)
    return newHeader
  }, [hasAdminPermission])

  const getData = useMemo(() => {
    let rlt: any = []

    const getVendorFragment = (item: any) => {
      const { amc } = item.info
      let rlt: any = [<div>{orderAppraisalVendorOptions[amc] || amc}</div>]
      const type = item.thirdPartyAPI?.type
      let identifier = ''

      if (AppraisalScopeThirdpartyTypes.includes(type)) {
        identifier = item.thirdPartyAPI.appraisalFileNumber
      }
      if (
        [Appraisal2ThridPartyType.Trinity, Appraisal2ThridPartyType.Inspectify].includes(type) &&
        item.thirdPartyAPI.orderID
      ) {
        const prefix = {
          [Appraisal2ThridPartyType.Trinity]: 'TR',
          [Appraisal2ThridPartyType.Inspectify]: 'IN',
        }[type]
        identifier = `${prefix}-${item.thirdPartyAPI.orderID}`
      }
      if (type === Appraisal2ThridPartyType.EliteValuation && item.thirdPartyAPI.OrderId)
        identifier = `EV-${item.thirdPartyAPI.FileId || item.thirdPartyAPI.OrderId}`
      if (type === Appraisal2ThridPartyType.VisionVMC && item.thirdPartyAPI.appraisalFileNumber)
        identifier = `VV-${item.thirdPartyAPI.appraisalFileNumber}`

      if (identifier) {
        rlt.push(
          <div
            className="text-shade-blue mt-2 hover:underline cursor-pointer italic font-bold"
            onClick={() => {
              setThirdpartyType(type)
              setThirdpartyData(item)
              setCancelEditFlag(cancelEditFlag + 1)
            }}
          >
            {identifier}
          </div>,
        )
      }
      return <div key={2}>{rlt}</div>
    }

    data.map((item) => {
      let orderType = item.info.orderType

      if (additionalTypes) {
        const integrationType = integrationTypeTitlesReversed[item.info.amc] as IntegrationType
        if (integrationType) {
          const newOrderType = additionalTypes?.[integrationType]?.[item.info.clientLenderAccount || 'main']?.orderType
          if (newOrderType && newOrderType[orderType]) orderType = newOrderType[orderType]
        }
      }

      let json: any = {
        ...item.info,
        vendor: getVendorFragment(item),
        orderDueDate: `<span><div class="border-b w-full mb-2">${formatDate(item.info.orderDate)}</div>${formatDate(
          item.info.clientDueDate,
        )}</span>`,
        orderTypeForm: `<span><div class="border-b w-full mb-2">${orderType}</div>${item.info.appraisalForm}</span>`,
        status: adminGroup ? renderStatus(item) : Appraisal2StatusType[item.status],
        id: item.id,
        createdBy: `<div>${formatTime(item.createdAt)}</div><div>${item.createdBy}</div>`,
        canUpdateRow: ['newOrder', 'onHold'].includes(item.status),
      }
      rlt.push(json)
    })
    return rlt
  }, [data, additionalTypes])

  useEffect(() => {
    let _orderIDs: any = ['All']
    data.map((item: any) => {
      // if (item.status !== 2) {
      _orderIDs.push(item.id)
      // }
    })
    setOrderIDs(_orderIDs)
  }, [data])

  const getCredentials = async () => {
    const loanNumber = Number(Api.getLoanNumber())
    const credentials: Record<IntegrationType, any[]> = {} as any
    const docTypes: Record<IntegrationType, string[]> = {} as any
    const jobTypes: Record<IntegrationType, string[]> = {} as any
    const additionalTypes: Record<IntegrationType, string[]> = {} as any
    const additionalInputs: Record<IntegrationType, any> = {} as any

    const data: Record<
      IntegrationType,
      { credentials: any; docTypes: string[]; jobTypes: string[]; additional: any; additionalInputs: any }
    > = await getBorrowerEliteCredential(loanNumber)
    Object.keys(data).forEach((_integrationType) => {
      const integrationType = _integrationType as IntegrationType
      credentials[integrationType] = data[integrationType].credentials
      docTypes[integrationType] = data[integrationType].docTypes
      jobTypes[integrationType] = data[integrationType].jobTypes
      additionalTypes[integrationType] = data[integrationType].additional
      additionalInputs[integrationType] = data[integrationType].additionalInputs
    })
    setCredentials(credentials)
    setDocTypes(docTypes)
    setJobTypes(jobTypes)
    setAdditionalTypes(additionalTypes)
    setAdditionalInputs(additionalInputs)
    return true
  }

  const initData = async (needReset = true, updated = false, currentId = 0) => {
    setLoading(true)
    const res = await getAppraisalInformationFeedV2()
    setAppraiser(res.appraiserEmail)
    if (res.success) {
      setData(res.data)
      needReset && setFeed(res.feed)
      setLogs(res.logs)
      if (updated) {
        const item = currentId ? res.data.find((v: any) => v.id == currentId) : res.data[res.data.length - 1]

        const type = item?.thirdPartyAPI.type
        if (Appraisal2ThridPartyTypeForInit.includes(type)) {
          setThirdpartyType(type)
          setThirdpartyData(item)
        }
      }
    }
    setLoading(false)
  }

  const initOverviewData = () => {
    const loanNumber = Api.getLoanNumber()
    loanOverviewAndStepLogic(loanNumber, '')
  }

  useEffect(() => {
    setLoanNumber()
    initOverviewData()
    initData()
    getCredentials()
  }, [])

  const getDateWithoutDivider = (values: Record<string, any>) => {
    let rlt = cloneDeep(values)
    Object.keys(rlt).map((key) => {
      if (inputs[key]?.inputType === 'divider') {
        delete rlt[key]
      }
    })
    return rlt
  }

  const onCancel = () => {
    setUpdateChangeMade(false)
  }

  const onSubmit = (currentId: any, values: Record<string, any>, finishEditing = true) => {
    const data = getDateWithoutDivider(values)
    return new Promise((resolve) => {
      setUpdateChangeMade(false)
      setLoading(true)

      const onCreateAppraisal = async (res: Record<string, any>) => {
        setLoading(false)
        values.id = res.data.id
        values.status = Appraisal2StatusType.newOrder
        values.createdBy = `<div>${formatTime(res.data.createdAt)}</div><div>${res.data.createdBy}</div>`
        await initData(finishEditing, true, currentId)
        resolve(values)
        setEditOrderID(values.id)
        handleFailedDocsNotification(res.thirdPartyResponse)
      }

      const onUpdateAppraisal = async (res: Record<string, any>) => {
        setLoading(false)
        values.createdBy = `<div>${formatTime(res.data.createdAt)}</div><div>${res.data.createdBy}</div>`
        resolve(values)
        setEditOrderID(currentId)
        handleFailedDocsNotification(res.thirdPartyResponse)
        initData(true, true, currentId)
      }

      if (!currentId) createAppraisalInfoV2(data).then(onCreateAppraisal)
      else updateAppraisalInfoV2(currentId, data).then(onUpdateAppraisal)
    })
  }

  const onCloseModal = () => {
    setThirdpartyType(null)
    setThirdpartyData(null)
    initData()
  }

  const logFragment = useMemo(() => {
    let rlt: any = []
    logs.map((item: any) => {
      let flag = true
      if (editOrderID !== 'All') {
        if (item.id != editOrderID) flag = false
      }
      if (flag) {
        rlt.push(item)
      }
    })
    return (
      <div className="overflow-auto mb-6 shadow-md rounded">
        <table className={`text-sm text-left text-gray-900 dark:text-gray-400 pl-6 w-full`}>
          <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th className="px-2 py-3">
                <span>Order</span>
                <br />
                <span>ID</span>
              </th>
              <th className="px-2 py-3">
                <span>Message</span>
                <br />
                <span>By/Date</span>
              </th>
            </tr>
          </thead>
          <tbody className="text-[14.5px] text-gray-900">
            {rlt.map((message: any, index: number) => (
              <tr key={index} className={`${index % 2 ? 'bg-gray-50' : ''}`}>
                <td className="p-2">{message.id}</td>
                <td className="p-2">
                  <div key={index} className="text-[14px]">
                    <div className="flex gap-2 items-center">
                      - <span className="font-variation-settings-600">{message.email}</span>
                      {Object.keys(message.notifiers || {}).length > 0 && (
                        <span className="cursor-pointer">
                          <Tooltip
                            message={JSON.stringify(message.notifiers)
                              .split(',')
                              .join('\n')
                              .replace('{', '')
                              .replace('}', '')}
                          >
                            <IdentificationIcon className="w-[15px] h-[15px]"></IdentificationIcon>
                          </Tooltip>
                        </span>
                      )}
                      <span className="text-gray-500">{formatTime(message.time)}</span>
                    </div>
                    <div className="ml-2 mt-1">{message.value}</div>
                    {message.files?.length > 0 && (
                      <div className="flex flex-wrap gap-2 ml-2">
                        <span className="text-[13px] ">Files:</span>
                        {message.files?.map((file: BaseFile, index: number) => (
                          <span
                            key={index}
                            className="flex gap-1 items-center hover:underline cursor-pointer text-[15px] font-medium text-shade-blue"
                            onClick={() => openS3Document(file.fileKey)}
                          >
                            <span className="text-[13px]">{file.name}, </span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }, [logs, editOrderID])

  const onRemove = async (id: any) => {
    return new Promise((resolve) => {
      setUpdateChangeMade(false)
      setLoading(true)
      deleteAppraisalInfoV2(id).then((res) => {
        if (res.success) {
          setLoading(false)
          resolve(true)
          setEditOrderID('All')
          initData()
        }
      })
    })
  }

  const onConfirmCancelOrder = async () => {
    const id = Number(editOrderID)
    setAction('cancellingOrder')
    const res = await cancelAppraisalInfoV2({ id })
    if (res.success) {
      let temp = cloneDeep(logs)
      temp.push(res.data)
      setLogs(temp)
    }
    setAction('')

    initData()

    return true
  }

  const renderEditOrderInput = () => {
    return (
      <Select
        className="min-w-[160px]"
        id={'orderID'}
        title={'Order ID'}
        options={orderIDs}
        key={'ordeID'}
        value={editOrderID}
        hasDefaultOption={true}
        onChange={(value) => setEditOrderID(value)}
      />
    )
  }

  const onChangeFiles = (documents: DocumentFile[]) => {
    setDocFiles(documents)
  }

  return (
    <div className="OrderAppraisals2-container px-2">
      <Overview title="Order Valuation" />
      <div className="max-w-screen-2xl m-auto mb-6">
        <div className="relative bg-white shadow1 rounded mb-4 p-4">
          <LayoutLoading show={loading} />
          <div className="relative flex gap-4 my-2 justify-between items-center mb-5">
            <div className="ml-2 text-[18px] font-variation-settings-600 flex items-center gap-2">
              Order Valuation
              <span className="text-[14px] ml-3">
                {loading && <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />}
              </span>
            </div>
          </div>
          {Object.keys(inputs).length > 0 && (
            <div className="grid gap-5 md:grid-cols-3 grid-cols-1 mt-4">
              <div className="md:col-span-1 h-fit">
                <div className="border-b font-variation-settings-[600] ml-2 flex flex-wrap items-end gap-2">
                  <span>Order Process</span>
                  <span>
                    <div className="-mb-3">{renderEditOrderInput()}</div>
                  </span>
                </div>
                {logFragment}
                <div className="px-3">
                  {renderEditOrderInput()}
                  <TextArea
                    className="pt-1"
                    title={'Message'}
                    key={'appraisalLog'}
                    rows={3}
                    value={editingLog}
                    onChange={setEditingLog}
                  />
                  <InputFileTable
                    className="pt-1 mb-4"
                    title={'Files'}
                    filePath={`loan/${Api.getLoanNumber()}/orderValuation`}
                    key={'commetFiles'}
                    name={'commetFiles'}
                    acceptFileTypes={'application/pdf'}
                    value={docFiles}
                    onChange={(value) => onChangeFiles(value)}
                    showHistory={() => {}}
                    showCategory={false}
                    showStatus={false}
                    multiple={true}
                  />
                  <div className="flex justify-center mt-4 flex-wrap gap-4">
                    <Button className="py-[5px]" onClick={() => createLog(false)} loading={action === 'createLog'}>
                      Send Message
                    </Button>
                    <Button
                      color="gray"
                      className="py-[5px]"
                      onClick={cancelOrder}
                      loading={action === 'cancellingOrder'}
                    >
                      Cancel Order
                    </Button>
                  </div>
                </div>
              </div>
              <div className="md:col-span-2 h-fit">
                <div className="border-b font-variation-settings-[600] ml-2">Orders</div>
                <div className="border-l">
                  <FormTable
                    ref={formRef}
                    cols={4}
                    header={getHeader}
                    inputs={inputs}
                    inputLogic={inputLogic}
                    data={getData}
                    onSubmit={onSubmit}
                    onRemove={onRemove}
                    onCancel={onCancel}
                    type={'orderAppraisal'}
                    permission={adminGroup ? 1 : 2}
                    addText={'Click Here to add New Order'}
                    cancelEditFlag={cancelEditFlag}
                    showFieldHistory={showHistory}
                    canOverride={hasPermission('CAN_OVERRIDE_ORDER_VALUATION')}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        {thirdpartyType && AppraisalScopeThirdpartyTypes.includes(thirdpartyType) && (
          <AppraisalNation
            onClose={onCloseModal}
            data={thirdpartyData}
            docTypes={docTypes?.[appraisal2IntegrationTypes[thirdpartyType]]}
            integrationType={appraisal2IntegrationTypes[thirdpartyType]}
          />
        )}
        {thirdpartyType == Appraisal2ThridPartyType.EliteValuation && (
          <EliteValuation onClose={onCloseModal} data={thirdpartyData} />
        )}
        {thirdpartyType == Appraisal2ThridPartyType.Trinity && <Trinity onClose={onCloseModal} data={thirdpartyData} />}
        {thirdpartyType == 'Inspectify' && <Inspectify onClose={onCloseModal} data={thirdpartyData} />}
      </div>
      <Prompt
        when={updateChangeMade}
        message={`You have added documents and made changes.\nAre you sure you want to leave without clicking the 'Update' button?`}
      />
      <Modal
        button={<span></span>}
        title={`Send Message to`}
        titleOkay="Confirm"
        titleCancel="Close"
        isOpen={messageToModal}
        lastUpdatedAt={Date.now()}
        loading={action === 'createLog'}
        onOk={() => createLog(true)}
        onClose={() => setMessageToModal(false)}
      >
        <div className="md:w-[560px]">
          <LoanPartiesEmailTo
            loanNumber={Number(Api.getLoanNumber())}
            value={notifiers}
            data={toEmails}
            onChange={onChangeEmail}
            loadParties
          />
        </div>
      </Modal>
    </div>
  )
}

export default AddAppraisalInformationV2
